import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { API_PATHS } from '../config/api-paths';
import { SmeTransactionsApi } from '../tokens/sme-transactions.token';
import { SmeTransaction, TransactionPage, TransactionView } from '../models/sme-transaction.model';
import { extractBackendErrorMessage } from '../util/backend-error';

/**
 * REST-only implementation of `SmeTransactionsApi` (EM-355, C2).
 *
 * Calls `GET /services/embedded/api/parties/{partyId}/transactions?accountId&view&limit&cursor`.
 *
 * Rides the existing Keycloak bearer (attached by `authInterceptor`). Holds the shared paged
 * transaction state the dashboard tabs consume: a tab switch calls {@link loadView} (resets +
 * loads page 0), load-more calls {@link loadMore} (appends the next page), and a currency switch
 * re-calls {@link loadView} with the new `accountId` (so the feed isolates to the active account's
 * currency, §15.2). Rule 07 / Rule 10 — no Mock fallback; Playwright specs intercept via
 * `page.route()`.
 *
 * Errors are surfaced via the {@link error} signal (Rule 09 — `extractBackendErrorMessage`); the
 * raw {@link list} primitive propagates the `HttpErrorResponse` to its caller.
 */
@Injectable()
export class SmeTransactionsRestService implements SmeTransactionsApi {
  private readonly http = inject(HttpClient);
  private readonly partiesBase = API_PATHS.PARTIES;

  private readonly itemsSig = signal<SmeTransaction[]>([]);
  private readonly nextCursorSig = signal<string | null>(null);
  private readonly totalCountSig = signal<number>(0);
  private readonly viewSig = signal<TransactionView>('past');
  private readonly loadingSig = signal<boolean>(false);
  private readonly errorSig = signal<string | null>(null);

  readonly items = this.itemsSig.asReadonly();
  readonly nextCursor = this.nextCursorSig.asReadonly();
  readonly totalCount = this.totalCountSig.asReadonly();
  readonly view = this.viewSig.asReadonly();
  readonly loading = this.loadingSig.asReadonly();
  readonly error = this.errorSig.asReadonly();

  list(partyId: number, accountId: number, view: TransactionView, limit?: number, cursor?: string): Observable<TransactionPage> {
    let params = new HttpParams().set('accountId', accountId).set('view', view);
    if (limit != null) {
      params = params.set('limit', limit);
    }
    if (cursor) {
      params = params.set('cursor', cursor);
    }
    return this.http.get<TransactionPage>(`${this.partiesBase}/${partyId}/transactions`, { params });
  }

  loadView(partyId: number, accountId: number, view: TransactionView, limit?: number): Observable<TransactionPage> {
    this.viewSig.set(view);
    this.errorSig.set(null);
    this.loadingSig.set(true);
    return this.list(partyId, accountId, view, limit).pipe(
      tap((page) => {
        this.itemsSig.set(page.items ?? []);
        this.nextCursorSig.set(page.nextCursor ?? null);
        this.totalCountSig.set(page.totalCount ?? 0);
      }),
      catchError((err: HttpErrorResponse) => this.handleError(err)),
      finalize(() => this.loadingSig.set(false)),
    );
  }

  loadMore(partyId: number, accountId: number, limit?: number): Observable<TransactionPage | null> {
    const cursor = this.nextCursorSig();
    if (!cursor) {
      return of(null);
    }
    this.errorSig.set(null);
    this.loadingSig.set(true);
    return this.list(partyId, accountId, this.viewSig(), limit, cursor).pipe(
      tap((page) => {
        this.itemsSig.update((current) => [...current, ...(page.items ?? [])]);
        this.nextCursorSig.set(page.nextCursor ?? null);
        this.totalCountSig.set(page.totalCount ?? this.totalCountSig());
      }),
      catchError((err: HttpErrorResponse) => this.handleError(err)),
      finalize(() => this.loadingSig.set(false)),
    );
  }

  private handleError(err: HttpErrorResponse): Observable<never> {
    this.errorSig.set(extractBackendErrorMessage(err, 'smeBorrower.payments.transactions.errors.loadFailed'));
    throw err;
  }
}
